import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { LoadedCatalog } from "../catalog.ts";
import type { Settings } from "../state/store.ts";
import type { ExamSession, ExamResult } from "../exam.ts";
import { createExam, gradeExam } from "../exam.ts";
import { useKeyboard } from "../state/useKeyboard.ts";
import { t } from "../i18n/ui.ts";
import { QuestionCard } from "./QuestionCard.tsx";
import { QuestionDetail } from "./QuestionDetail.tsx";
import { ExamToolbar } from "./ExamToolbar.tsx";

interface Props {
  catalog: LoadedCatalog;
  settings: Settings;
  /** Run this specific official sheet instead of a random one. */
  setId?: number;
  /** Official number of the question to show, taken from the URL. */
  question?: string;
  onQuestionChange: (number: string) => void;
  /**
   * Pins the URL to a concrete sheet and question on arrival, so the address
   * always names what is on screen and reloading is stable.
   */
  onPin: (setId: number, question: string) => void;
  isMarked: (id: number) => boolean;
  onToggleMark: (id: number) => void;
  onFinish: (session: ExamSession, result: ExamResult) => void;
  onExit: () => void;
  onSwapLanguages: () => void;
  onCycleSecondary: () => void;
  onShowHelp: () => void;
}

/**
 * Runs one 30-question exam sheet. Answers stay hidden until the whole sheet is
 * submitted, matching the real test.
 */
export function ExamRunner({
  catalog, settings, setId, question, onQuestionChange, onPin,
  isMarked, onToggleMark,
  onFinish, onExit, onSwapLanguages, onCycleSecondary, onShowHelp,
}: Props) {
  const [session, setSession] = useState<ExamSession>(() =>
    createExam(catalog, undefined, setId)
  );
  const [confirming, setConfirming] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [detail, setDetail] = useState(false);
  const [playSignal, setPlaySignal] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const lang = settings.primary;

  const items = session.items;
  // The URL names the question; fall back to the first one if it names none,
  // or one that is not on this sheet.
  const found = items.findIndex((item) => item.question.number === question);
  const safeIndex = found >= 0 ? found : 0;
  const current = items[safeIndex]!;

  // Put the dealt sheet and the current question into the address on arrival,
  // so it always names what is on screen. Replacing rather than pushing keeps
  // it out of the history, and the guard stops it overwriting later moves.
  const pinned = useRef(false);
  useEffect(() => {
    if (pinned.current) return;
    if (setId !== undefined && question) return;
    pinned.current = true;
    onPin(session.set.id, current.question.number);
  }, [setId, question, session.set.id, current.question.number, onPin]);
  const answeredCount = useMemo(() => items.filter((i) => i.touched).length, [items]);

  const patchCurrent = useCallback(
    (patch: Partial<(typeof items)[number]>) => {
      setSession((s) => ({
        ...s,
        items: s.items.map((item, i) => (i === safeIndex ? { ...item, ...patch } : item)),
      }));
    },
    [safeIndex]
  );

  const toggle = useCallback(
    (answerIndex: number) => {
      const selected = current.selected.includes(answerIndex)
        ? current.selected.filter((i) => i !== answerIndex)
        : [...current.selected, answerIndex];
      patchCurrent({ selected, touched: selected.length > 0 });
    },
    [current, patchCurrent]
  );

  const type = useCallback(
    (value: string) => patchCurrent({ typed: value, touched: value.trim() !== "" }),
    [patchCurrent]
  );

  const go = useCallback(
    (to: number) => {
      const clamped = Math.max(0, Math.min(items.length - 1, to));
      if (clamped !== safeIndex) onQuestionChange(items[clamped]!.question.number);
    },
    [items, items.length, safeIndex, onQuestionChange]
  );

  // Move focus to the panel on every change so screen readers announce it,
  // including when the change came from the browser's back button. Each
  // question starts untranslated, as in the real exam.
  useEffect(() => {
    setTranslating(false);
    setDetail(false);
    cardRef.current?.focus();
  }, [safeIndex]);

  useKeyboard({
    arrowright: () => go(safeIndex + 1),
    n: () => go(safeIndex + 1),
    arrowleft: () => go(safeIndex - 1),
    p: () => go(safeIndex - 1),
    enter: () =>
      safeIndex === items.length - 1 ? setConfirming(true) : go(safeIndex + 1),
    "1": () => current.question.answers[0] && toggle(0),
    "2": () => current.question.answers[1] && toggle(1),
    "3": () => current.question.answers[2] && toggle(2),
    t: () => setTranslating((v) => !v),
    v: () => setPlaySignal((n) => n + 1),
    m: () => onToggleMark(current.question.id),
    s: onSwapLanguages,
    l: onCycleSecondary,
    f: () => setConfirming(true),
    escape: () => (confirming ? setConfirming(false) : onExit()),
    "?": onShowHelp,
  });

  const open = items.length - answeredCount;

  return (
    <div className="tsheet">
      <QuestionCard
        key={current.question.id}
        ref={cardRef}
        question={current.question}
        settings={settings}
        selected={current.selected}
        typed={current.typed}
        onToggle={toggle}
        onType={type}
        revealed={false}
        showExplanation={false}
        showSecondary={translating}
        positionLabel={`${safeIndex + 1} / ${items.length}`}
        playSignal={playSignal}
        onClose={onExit}
        onInfo={() => setDetail(true)}
      />

      <QuestionDetail
        open={detail}
        lang={lang}
        question={current.question}
        catalog={catalog}
        onClose={() => setDetail(false)}
      />

      <ExamToolbar
        lang={lang}
        basicLabel={current.question.basic ? t(lang, "basicKnowledge") : ""}
        classLabel={session.licenceClass.name}
        remaining={open}
        marked={isMarked(current.question.id)}
        onToggleMark={() => onToggleMark(current.question.id)}
        extraActions={
          settings.secondary && settings.secondary !== settings.primary
            ? [{
                label: t(lang, translating ? "hideTranslation" : "showTranslation"),
                onClick: () => setTranslating((v) => !v),
                pressed: translating,
              }]
            : undefined
        }
        onEvaluate={() => setConfirming(true)}
        onNext={() => go(safeIndex + 1)}
        nextDisabled={safeIndex === items.length - 1}
        onPrev={() => go(safeIndex - 1)}
        prevDisabled={safeIndex === 0}
        positions={items.map((item, i) => ({
          index: i,
          label: String(i + 1),
          answered: item.touched,
          current: i === safeIndex,
          marked: isMarked(item.question.id),
          video: item.question.media?.kind === "video",
        }))}
        onJump={go}
      />

      {confirming && (
        <ConfirmFinish
          lang={lang}
          open={open}
          onCancel={() => setConfirming(false)}
          onConfirm={() => onFinish(session, gradeExam(session))}
        />
      )}
    </div>
  );
}

function ConfirmFinish({
  lang, open, onCancel, onConfirm,
}: {
  lang: Parameters<typeof t>[0];
  open: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="scrim" role="presentation" onClick={onCancel}>
      <div
        className="dialog dialog--inline"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="finish-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="finish-title">
          {open > 0 ? t(lang, "confirmFinishOpen", { n: open }) : t(lang, "confirmFinish")}
        </h2>
        <div className="dialog__actions">
          <button type="button" className="btn" onClick={onCancel}>
            {t(lang, "cancel")}
          </button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} autoFocus>
            {t(lang, "confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
